<template>
  <div class="page-wrapper">
    <DashboardHeader 
      @profile-updated="handleProfileUpdated"
      @openEditProfile="openEditProfile"
      @openManageSubscription="openManageSubscription"
      ref="dashboardHeader"
    />
    <div class="dashboard-container">
      <!-- Toggle button for smaller screens (only for MASTER users) -->
      <button 
        v-if="isMasterUser"
        class="sidebar-toggle" 
        :class="{ 'sidebar-open': sidebarOpen }"
        @click="toggleSidebar"
        :aria-label="sidebarOpen ? 'Close sidebar' : 'Open sidebar'"
        :aria-expanded="sidebarOpen"
      >
        <span class="hamburger" :class="{ active: sidebarOpen }">
          <span class="line"></span>
          <span class="line"></span>
          <span class="line"></span>
        </span>
      </button>

      <!-- Sidebar (only for MASTER users) -->
      <aside 
        v-if="isMasterUser"
        class="sidebar" 
        :class="{ open: sidebarOpen }"
        role="navigation"
        aria-label="Main navigation"
      >
        <div class="sidebar-header">
          <h2 class="sidebar-title">Navigation</h2>
          <button 
            class="sidebar-close-btn" 
            @click="toggleSidebar"
            :aria-label="'Close sidebar'"
          >
            <span class="hamburger active">
              <span class="line"></span>
              <span class="line"></span>
              <span class="line"></span>
            </span>
          </button>
        </div>
        
        <nav class="sidebar-nav">
          <ul class="nav-list">
            <li class="nav-item" :class="{ active: activeComponent === 'MainDashboardView' }">
              <button 
                class="nav-button"
                @click="setActiveComponent('MainDashboardView')"
                :aria-current="activeComponent === 'MainDashboardView' ? 'page' : undefined"
              >
                <div class="nav-icon">
                  <font-awesome-icon icon="chart-bar" />
                </div>
                <span class="nav-text">Dashboard</span>
                <div class="nav-indicator"></div>
              </button>
            </li>
            
            <li class="nav-item" :class="{ active: activeComponent === 'SubUsersView' }">
              <button 
                class="nav-button"
                @click="setActiveComponent('SubUsersView')"
                :aria-current="activeComponent === 'SubUsersView' ? 'page' : undefined"
              >
                <div class="nav-icon">
                  <font-awesome-icon icon="users" />
                </div>
                <span class="nav-text">Sub Users</span>
                <div class="nav-indicator"></div>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <!-- Overlay (only for MASTER users) -->
      <div 
        v-if="isMasterUser"
        class="overlay" 
        :class="{ open: sidebarOpen }" 
        @click="toggleSidebar"
        role="button"
        tabindex="0"
        :aria-label="'Close sidebar'"
      ></div>

      <!-- Main Content -->
      <main class="main-content" :class="{ 'no-sidebar': !isMasterUser }">
        <component :is="activeComponent"></component>
      </main>
    </div>

    <!-- Edit Profile Modal -->
    <EditProfile 
      v-if="showEditProfile" 
      :admin-profile-data="adminProfileData"
      @close="closeEditProfile"
      @profile-updated="handleProfileUpdated"
    />

    <ManageSubscription
      v-if="showManageSubscription"
      :admin-profile-data="adminProfileData"
      @close="closeManageSubscription"
    />
  </div>
</template>

<script>
import DashboardHeader from './DashboardHeader.vue';
import MainDashboardView from './MainDashboardView.vue';
import SubUsersView from './SubUsersView.vue';
import EditProfile from './EditProfile.vue';
import ManageSubscription from './ManageSubscription.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChartBar, faUsers } from '@fortawesome/free-solid-svg-icons';

library.add(faChartBar, faUsers);

export default {
  name: 'DashboardPage',
  components: {
    DashboardHeader,
    FontAwesomeIcon,
    MainDashboardView,
    SubUsersView,
    EditProfile,
    ManageSubscription,
  },
  data() {
    return {
      sidebarOpen: false,
      activeComponent: 'MainDashboardView',
      showEditProfile: false,
      showManageSubscription: false,
      adminProfileData: {},
    };
  },
  computed: {
    isMasterUser() {
      return this.adminProfileData?.userType === 'MASTER';
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
      // Prevent body scroll when sidebar is open on mobile
      if (this.sidebarOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    setActiveComponent(componentName) {
      this.activeComponent = componentName;
      // Save active component to localStorage
      localStorage.setItem('DASHBOARD_ACTIVE_COMPONENT', componentName);
      if (this.sidebarOpen) {
        this.toggleSidebar();
      }
    },
    loadActiveComponent() {
      try {
        const storedComponent = localStorage.getItem('DASHBOARD_ACTIVE_COMPONENT');
        // Validate that the stored component is a valid component name
        if (storedComponent && (storedComponent === 'MainDashboardView' || storedComponent === 'SubUsersView')) {
          this.activeComponent = storedComponent;
        }
      } catch (error) {
        console.error('Failed to load active component:', error);
      }
    },
    openEditProfile() {
      this.showEditProfile = true;
    },
    closeEditProfile() {
      this.showEditProfile = false;
    },
    openManageSubscription() {
      this.showManageSubscription = true;
      document.body.style.overflow = 'hidden';
    },
    closeManageSubscription() {
      this.showManageSubscription = false;
      document.body.style.overflow = '';
    },
    handleProfileUpdated(updatedData) {
      this.adminProfileData = updatedData;
      // Update the DashboardHeader's adminProfileData directly to avoid circular events
      if (this.$refs.dashboardHeader) {
        this.$refs.dashboardHeader.adminProfileData = updatedData;
      }
    },
    loadAdminProfileData() {
      try {
        const storedData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = storedData ? JSON.parse(storedData) : {};
      } catch (error) {
        console.error('Failed to parse admin profile data:', error);
        this.adminProfileData = {};
      }
    },
  },
  mounted() {
    this.loadAdminProfileData();
    this.loadActiveComponent();
  },
  beforeUnmount() {
    // Clean up body overflow when component is destroyed
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* Base styles */
.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.dashboard-container {
  display: flex;
  flex: 1;
  background-color: #f8fafc;
  margin-top: 80px;
  position: relative;
}

/* Sidebar Toggle Button */
.sidebar-toggle {
  display: none;
  position: fixed;
  top: 80px;
  left: 20px;
  z-index: 999;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  font-size: 16px;
  cursor: pointer;
  color: #5b2e8e;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-toggle.sidebar-open {
  left: 280px;
}

.sidebar-toggle:hover {
  background: #5b2e8e;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.25);
}

.sidebar-toggle:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.2);
}

/* Hamburger Animation */
.hamburger {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 20px;
  height: 16px;
  transition: all 0.3s ease;
}

.hamburger .line {
  width: 100%;
  height: 2px;
  background-color: currentColor;
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}

.hamburger.active .line:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.hamburger.active .line:nth-child(2) {
  opacity: 0;
  transform: scale(0);
}

.hamburger.active .line:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* Sidebar */
.sidebar {
  width: 280px;
  background: linear-gradient(135deg, #5b2e8e 0%, #7a49b0 100%);
  color: #ffffff;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 80px;
  left: 0;
  height: calc(100vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 100;
}

.sidebar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%);
  pointer-events: none;
}

/* Sidebar Header */
.sidebar-header {
  padding: 24px 20px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
  letter-spacing: 0.5px;
  flex: 1;
}

/* Sidebar Close Button (inside sidebar) */
.sidebar-close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  color: #ffffff;
  display: none;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.sidebar-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.sidebar-close-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.sidebar-close-btn .hamburger {
  width: 18px;
  height: 14px;
}

/* Navigation */
.sidebar-nav {
  padding: 0 12px;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin-bottom: 4px;
}

.nav-button {
  width: 100%;
  background: none;
  border: none;
  padding: 16px 20px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  display: flex;
  align-items: center;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  transform: translateX(4px);
}

.nav-button:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.nav-item.active .nav-button {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nav-icon {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.3s ease;
}

.nav-item.active .nav-icon {
  transform: scale(1.1);
}

.nav-text {
  flex: 1;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.nav-indicator {
  width: 4px;
  height: 20px;
  background: #ffffff;
  border-radius: 2px;
  opacity: 0;
  transform: scaleY(0);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-item.active .nav-indicator {
  opacity: 1;
  transform: scaleY(1);
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  position: relative;
  transition: all 0.3s ease;
  min-width: 0; /* Prevents flex item from overflowing */
  margin-left: 280px;
}

.main-content.no-sidebar {
  margin-left: 0;
}

/* Overlay */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(2px);
}

.overlay.open {
  opacity: 1;
  visibility: visible;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .sidebar-toggle {
    display: block;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }

  .sidebar {
    position: fixed;
    top: 80px;
    left: -260px;
    width: 260px;
    height: calc(100vh - 80px);
    z-index: 1000;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .sidebar.open {
    left: 0;
  }
  
  .main-content:not(.no-sidebar) {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: -260px;
    height: 100vh;
    width: 260px;
  }

  .sidebar.open {
    left: 0;
  }

  .dashboard-container {
    margin-top: 60px;
  }

  .sidebar-toggle {
    top: 60px;
    left: 20px;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }
}

@media (max-width: 480px) {
  .sidebar {
    width: 240px;
    left: -240px;
  }
  
  .sidebar.open {
    left: 0;
  }
  
  .sidebar-toggle {
    top: 70px;
    left: 15px;
    padding: 10px;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }
}

@media (max-width: 360px) {
  .sidebar {
    width: 220px;
    left: -220px;
  }
  
  .sidebar.open {
    left: 0;
  }
  
  .sidebar-toggle {
    top: 65px;
    left: 10px;
    padding: 8px;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }
  
  .hamburger {
    width: 18px;
    height: 14px;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .sidebar,
  .nav-button,
  .hamburger,
  .overlay {
    transition: none;
  }
}

/* Focus visible for better accessibility */
.nav-button:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.sidebar-toggle:focus-visible {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}
</style>